<template>
    <!-- 
    UI Quicktable Select | quicktable-select

    @Requiere 
    + ui-input-handler@1.21.0 o superior
    + ui-edit-auto-modal.vue@1.18.0 o superior
    
    @Version 1.4.0
    Recibe "args" desde quicktable => Automodal => UiInputHanlder => QuicktableSelect
    Recibe "formAsArgs" y "fullFormGetter" como función para enviar directamente a query de consulta con sustitución desde "formAsArgs"; 
    por ejemplo en el archetype siguiente:

        {
            type: 'quicktableSelect',
            key: 'quicktableelement_id',
            label: 'Quicktable picker',
            quicktable: () => import('@/views/Quicktable/Quicktable.vue'),
            filters: () => {return []}, // Especifica filtros vacíos los filtros, pero se puede mandar otra colección de filtros distinta.
            formAsArgs: {
                role: 'role_related',
            }
        }

    El Quicktable.vue en no mostrará filtros, pues están vacíados, pero enviará además de lo que venge en args el parámetro role, con el contenido del formulario
    del automodal actual en el parámetro 'role_related'. 

    @Version 1.3.0
    Permite el parámetro readonly y required respectivamente. En readonly no permite cambios, 
    en required true obliga a seleccionar siempre algún valor. En caso de readonly = false (Por defecto)
    permite limpiar la selección.

    @Version 1.2.2
    Corrección de estilo de Select

    @Version 1.2.1
    Bugfix (Conflicto en cOptions mutación de cadena valor de origen y asignación de etiqueta.)

    @Version 1.2
    Agregada propiedad kIndex
    
    @Version 1.1
    Fix con ui-input-handler
    
    @Version 1.0
    Usado en ui-input-handler@1.16.x

    @author Daniel Lepe
    -->
    <div class="quicktableSelect">

        <b-input-group>
            <select v-model="workingValue" class="custom-select" :key="'quicktable-select' + workingValue"
                @click="triggerModal" 
                @mousedown.prevent="() => { }">
                <option>{{ unSelectedLabel }}</option>
                <option v-for="opt in cOptions" :value="opt[valueName]" :key="'opt-' + opt[valueName]">
                    {{  opt[labelName] }}
                </option>
            </select>
            <b-input-group-append v-if="!readonly">
                <b-button variant="info" v-if="!required && !isEmpty" @click="setClean"><i class="fa fa-times"></i></b-button>
                <b-button v-if="!readonly" @click="triggerModal"><i class="fa fa-search"></i></b-button>
            </b-input-group-append>
        </b-input-group>

        <template v-if="debug">
            <code><pre>{{ cOptions }}</pre></code>
            <code><pre>Value: {{ value }}</pre></code>
            <code><pre>WorkingValue: {{ workingValue }}</pre></code>
            <code><pre>{{ workingItem }}</pre></code>
        </template>

        <b-modal v-model="displayModal" 
            :size="size" 
            :title="label" 
            :hide-footer="hideButtons"
            scrollable 
            modal-class="quicktableSelectModal"
            no-enforce-focus 
            no-body 
            no-gutter 
            no-close-on-backdrop 
            no-close-on-esc >

            <component v-model="workingValue" :key="cArgsMd5"
                :is="quicktable" 
                :valueName="valueName" 
                :labelName="labelName" 
                :args="cArgs"
                :quicktableFilters="filters"
                select-mode
                hide-navigation 
                @setItem="setWorkingItem" 
                @refreshed="refreshed" />


            <template v-slot:modal-footer="{ cancel }" v-if="!hideButtons">
                <b-button-group size="sm">
                    <b-button @click="cancel()" v-if="cancelLabel">{{ cancelLabel }}</b-button>
                    <slot name="extra-buttons"></slot>
                    <b-button @click="triggerSelect($event)" variant="primary" v-if="applyButton"
                        :disabled="!workingValue">
                        {{ applyButton }}
                    </b-button>
                </b-button-group>
            </template>

        </b-modal>
    </div>

</template>

<script>
import _ from 'lodash'
import md5 from 'js-md5'
export default {
    name: 'quicktableSelect',
    props: {
        filters: {
            type: [Object, Function]
        },
        fullFormGetter: {
			type: Function
		},
        formAsArgs: {
            type: Object
        },  
        required: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        kIndex: {
			type: [Number, String],
		},
        fArchKey: {
            type: String
        },
        fnToSelectFromMainQueryItem:  {
			type: Function
		},
        debug: {
            type: Boolean,
            default: false
        },
        unSelectedLabel: {
            type: [String],
            default: 'Unselected'
        },
        cancelLabel: {
            type: [String, Boolean],
            default: 'Cancel'
        },
        applyButton: {
            type: [String, Boolean, Number],
            default: "Select",
        },
        hideButtons: {
            type: Boolean,
            default: false
        },
        label: {
            type: String,
            default: 'Options'
        },
        value: {
            type: [String, Number]
        },
        valueName: {
            type: String,
            default: 'id'
        },
        labelName: {
            type: String,
            default: 'label'
        },
        options: {
            type: Array,
            default: () => { return [] }
        },
        quicktable: {
            type: [Object, Function],
            required: true
        },
        size: {
            type: String,
            default: 'lg'
        },
        args: {
            type: [Array, Object]
        }
    },
    computed: {
        cArgsMd5(){
            return md5(JSON.stringify(this.cArgs))
        },
        cArgs(){
            let args = _.clone(this.args),
                formAsArgs = {};
            _.forEach(this.formAsArgs, (formKeyName, argName) => {
                if(formKeyName in this.fetchedForm)
                    formAsArgs[argName] = this.fetchedForm[formKeyName];
            })
            const cArgs = {...args, ...formAsArgs};
            return cArgs
        },
        isEmpty(){
            return (!this.workingValue || this.workingValue == '')
        },
        cOptions() {
            // @Version 1.2.1 foundFromMainQuery solo debe ser accesible si no hay  datos en localOptions:
            let foundFromMainQuery = [];
            if(_.isFunction(this.fnToSelectFromMainQueryItem)){
                let foundValue = this.fnToSelectFromMainQueryItem(this.value, this.fArchKey, this.kIndex);
                if(foundValue){
                    let item = {};
                    item[this.valueName] = _.clone(this.value);
                    item[this.labelName] = foundValue;
                    foundFromMainQuery.push(item)
                }
            }
            return (this.localOptions.length) ? _.concat(this.options, this.localOptions) : _.concat(foundFromMainQuery, this.options);
        }
    },
    data: () => {
        return {
            localId: null,
            workingValue: null,
            localOptions: [],
            workingItem: null,
            fetchedForm: {
                // Se cargan dinámicamente desde el objeto padre de formulario con 
                // getFormAndArgs(). Se mandan al componente quickSelect via cArgs
            },
            displayModal: false
        }
    },
    beforeMount() {
        this.localId = _.uniqueId('quicktable-select-')
        this.workingValue = _.clone(this.value)
    },
    methods: {
        getFormAndArgs(){
            if(_.isFunction(this.fullFormGetter))
                this.fetchedForm = this.fullFormGetter()
        },
        refreshed() {
            this.workingValue = null;
        },
        setWorkingItem(item) {
            try {
                let existingIndex = _.findIndex(this.cOptions, opt => {
                    return (opt[this.valueName] == item[this.valueName])
                });
                if (existingIndex === -1) {
                    this.localOptions.push(item)
                } else {
                    this.localOptions[existingIndex] = item
                }
            } catch (e) {
                console.error(e)
            }
        },
        triggerSelect() {
            this.$emit('input', _.clone(this.workingValue))
            this.$nextTick(() => {
                this.displayModal = false;
                this.$emit('changed')
            })
        },
        setClean(){
            let emptyObj  = {}
            emptyObj[this.valueName] = '';
            emptyObj[this.labelName] = '';
            this.setWorkingItem(emptyObj)
            this.workingValue = '';
            this.triggerSelect()
        },
        triggerModal() {
            try {
                this.getFormAndArgs();
                if(!this.readonly)
                    this.displayModal = true
            } catch (e) {
                console.error(e)
            }
        }
    },
    watch: {
        value() {
            if (this.value != this.workingValue) {
                this.workingValue = _.clone(this.value)
            }
        }
    }
}
</script>

<style lang="less">
.quicktableSelectModal {

    .card {
        /* margin-bottom: 1.5rem; */
        margin-bottom: 0;
    }

    .modal-body {
        /* padding: 1rem; */
        padding: 0;
    }

    .card {
        /* border: 1px solid #c8ced3; */
        /* border-radius: 0.25rem; */
        border: 0px solid #c8ced3;
        border-radius: 0;
    }
}
.quicktableSelect {
    SELECT.custom-select {
        appearance: none;
        background: transparent;
        background-image: none;
    
        &::-ms-expand {
            display: none;
        }
    }
}
</style>